import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  LuShieldCheck, LuSearch, LuChevronDown, LuChevronRight, LuArrowLeft, LuArrowRight,
} from 'react-icons/lu';

const ACTION_STYLE = {
  CREATE:      { bg: 'var(--success-soft)', color: 'var(--success)', label: 'Criação' },
  UPDATE:      { bg: 'var(--info-soft)', color: 'var(--info)', label: 'Edição' },
  DELETE:      { bg: 'var(--danger-soft)', color: 'var(--danger)', label: 'Exclusão' },
  LOGIN:       { bg: 'var(--purple-soft)', color: 'var(--purple)', label: 'Login' },
  LOGIN_FAIL:  { bg: 'var(--warning-soft)', color: 'var(--warning)', label: 'Login negado' },
  LOGOUT:      { bg: 'var(--surface-3)', color: 'var(--text-2)', label: 'Logout' },
  '2FA_ENABLE':  { bg: 'var(--success-soft)', color: 'var(--success)', label: '2FA ativado' },
  '2FA_DISABLE': { bg: 'var(--danger-soft)', color: 'var(--danger)', label: '2FA desativado' },
  PASSWORD_CHANGE: { bg: 'var(--info-soft)', color: 'var(--info)', label: 'Senha alterada' },
  FORCE_LOGOUT:    { bg: 'var(--danger-soft)', color: 'var(--danger)', label: 'Logout forçado' },
};

const ENTITY_LABEL = {
  assessment:                 'Avaliação',
  user:                       'Usuário',
  company:                    'Empresa',
  department:                 'Departamento',
  employee:                   'Funcionário',
  timeRecord:                 'Ponto',
  backup:                     'Backup',
  holiday:                    'Feriado',
  indicator:                  'Indicador',
  indicatorConfig:            'Config. Indicador',
  evaluation:                 'Avaliação Estruturada',
  auditLog:                   'Log de Auditoria',
  rolePermission:             'Permissões',
  clinicalDailyRecord:        'Lançamento Diário (Metas)',
  clinicalGoalType:           'Catálogo de Tipos (Metas)',
  clinicalGoal:               'Meta',
  clinicalProductionCounter:  'Contador de Produção',
  clinicalExpense:            'Despesa (Metas)',
};

const ActionBadge = ({ action }) => {
  const s = ACTION_STYLE[action] || { bg: 'var(--surface-3)', color: 'var(--text-2)', label: action };
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontWeight: 700, fontSize: 11,
      padding: '2px 10px', borderRadius: 20,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};

const DetailsRow = ({ details }) => {
  const [open, setOpen] = useState(false);
  if (!details) return <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>;
  return (
    <div>
      <button onClick={() => setOpen(!open)} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 12, color: 'var(--text-2)', padding: 0,
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {open ? <LuChevronDown size={11} /> : <LuChevronRight size={11} />}
        Ver detalhes
      </button>
      {open && (
        <pre style={{
          marginTop: 6, padding: '8px 12px', borderRadius: 6,
          background: 'var(--surface-2)', fontSize: 11, color: 'var(--text-2)',
          maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap',
          wordBreak: 'break-all', border: '1px solid var(--border)',
        }}>
          {JSON.stringify(details, null, 2)}
        </pre>
      )}
    </div>
  );
};

const fmt = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const EMPTY_FILTERS = { action: '', entity: '', username: '', from: '', to: '' };

export default function AuditPage() {
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [pages,   setPages]   = useState(1);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const LIMIT = 20;

  const load = useCallback(async (f, p) => {
    setLoading(true);
    try {
      const params = { ...f, page: p, limit: LIMIT };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const r = await api.get('/audit-logs', { params });
      setRows(r.data.rows);
      setTotal(r.data.total);
      setPages(r.data.pages);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(applied, page); }, [applied, page, load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setApplied({ ...filters });
  };

  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  const f = (field) => (e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LuShieldCheck size={20} color="var(--accent)" />
            Relatório de Auditoria
          </div>
          <div className="page-card-subtitle">Histórico de todas as ações realizadas no sistema</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-3)', alignSelf: 'flex-end' }}>
          {total} registro{total !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Filtros */}
      <form onSubmit={handleSearch} style={{
        background: 'var(--surface-2)', borderRadius: 8, padding: '16px 20px',
        marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase' }}>Ação</label>
          <select className="form-control" style={{ minWidth: 140, fontSize: 13 }} value={filters.action} onChange={f('action')}>
            <option value="">Todas</option>
            {Object.entries(ACTION_STYLE).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase' }}>Entidade</label>
          <select className="form-control" style={{ minWidth: 160, fontSize: 13 }} value={filters.entity} onChange={f('entity')}>
            <option value="">Todas</option>
            {Object.entries(ENTITY_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase' }}>Usuário</label>
          <input className="form-control" style={{ minWidth: 160, fontSize: 13 }}
            placeholder="nome do usuário" value={filters.username} onChange={f('username')} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase' }}>De</label>
          <input type="date" className="form-control" style={{ fontSize: 13 }} value={filters.from} onChange={f('from')} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase' }}>Até</label>
          <input type="date" className="form-control" style={{ fontSize: 13 }} value={filters.to} onChange={f('to')} />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="btn-salvar" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <LuSearch size={13} /> Buscar
          </button>
          <button type="button" className="btn-excluir" onClick={handleClear}>
            Limpar
          </button>
        </div>
      </form>

      {/* Tabela */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>Carregando...</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)', fontSize: 14 }}>
          Nenhum registro encontrado para os filtros aplicados.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Data / Hora', 'Usuário', 'Ação', 'Entidade', 'ID', 'IP', 'Detalhes'].map((h) => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
                    textTransform: 'uppercase', borderBottom: '2px solid var(--border)',
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id} style={{ background: i % 2 === 0 ? 'var(--surface-1)' : 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>
                    {fmt(row.created_at)}
                  </td>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text-1)' }}>
                    {row.username || '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <ActionBadge action={row.action} />
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-2)' }}>
                    {ENTITY_LABEL[row.entity] || row.entity || '—'}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-3)' }}>
                    {row.entity_id || '—'}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-3)', fontFamily: 'monospace', fontSize: 12 }}>
                    {row.ip_address || '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <DetailsRow details={row.details} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginação */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <button className="btn-editar" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            <LuArrowLeft />
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
            Página {page} de {pages} — {total} registros
          </span>
          <button className="btn-editar" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}>
            <LuArrowRight />
          </button>
        </div>
      )}
    </div>
  );
}
